import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { YYYYMMDDSchema } from '../../models';

extendZodWithOpenApi(z);
const c = initContract();

export const apiTimelyMemoriesRoute = c.router({
  deleteUserMemories: {
    method: 'POST',
    path: `/1.0/timely/memories/delete`,
    body: z.object({
      timely_user_id: z.number().int().min(1).openapi({
        description: 'The Timely user ID whose memories should be deleted',
        example: 2302306,
      }),
      until_date: YYYYMMDDSchema.openapi({
        description:
          'Delete memories with started_at before this date (exclusive). Format: YYYY-MM-DD in UTC.',
        example: '2024-01-15',
      }),
    }),
    responses: {
      200: z.object({
        message: z.string().openapi({
          description: 'Status message',
          example: 'Deletion started',
        }),
        run_id: z.string().nullable().openapi({
          description: 'Trigger.dev run ID for polling status. Null if no TIC profile found.',
          example: 'run_abc123',
        }),
      }),
      502: z.object({
        message: z.string().openapi({
          description: 'Error message when the upstream Trigger.dev service fails',
          example: 'Failed to start deletion task',
        }),
        run_id: z.null().openapi({
          description: 'Always null on failure',
        }),
      }),
    },
    summary: 'Trigger async memory deletion for a TIC user up to a specific date',
    description: `Enqueues async deletion of all memories for a specific TIC user with started_at before the specified date.

Returns a run_id that can be used to poll /1.0/timely/memories/delete/status for completion.

**Authentication**: Requires \`INTERNAL_TIMELY_WEBHOOK_API_KEY\` in Authorization header.`,
  },

  getDeleteMemoriesStatus: {
    method: 'GET',
    path: `/1.0/timely/memories/delete/status`,
    query: z.object({
      run_id: z.string().openapi({
        description: 'The Trigger.dev run ID returned by deleteUserMemories',
        example: 'run_abc123',
      }),
    }),
    responses: {
      200: z.object({
        status: z.enum(['running', 'completed', 'failed']).openapi({
          description: 'Current status of the deletion run',
          example: 'completed',
        }),
        deleted_count: z.number().int().openapi({
          description: 'Number of memories deleted (0 if not yet completed)',
          example: 42,
        }),
        message: z.string().openapi({
          description: 'Human-readable status message',
          example: 'Deletion completed: 42 memories removed',
        }),
      }),
      404: z.object({
        status: z.literal('failed').openapi({
          description: 'Always "failed" for not-found runs',
        }),
        deleted_count: z.literal(0).openapi({
          description: 'Always 0 for not-found runs',
        }),
        message: z.string().openapi({
          description: 'Error message',
          example: 'Run not found: run_abc123',
        }),
      }),
    },
    summary: 'Poll status of an async memory deletion run',
    description: `Returns the current status of a memory deletion task triggered by deleteUserMemories.

**Authentication**: Requires \`INTERNAL_TIMELY_WEBHOOK_API_KEY\` in Authorization header.`,
  },
});
